import { useEffect, useState } from "react";
import { DaySchedule, Scheduling, WeekSchedule } from "../models/schedule";
import {
  fetchConsultantSchedulingsByMonthAndYear,
  fetchUnviewedSchedulings,
} from "../api/fetchScheduling";
import { getMaintenanceWeekSchedule } from "../services/consultantScheduleService";
import SchedulingCard from "../pages/dealership/consultant/maintenanceControl/components/schedule/SchedulingCard";
import { useNavigate } from "react-router-dom";
import { checkSchedulingAsViewed } from "../api/appointments";
import { fetchOccurenceList } from "../api/fetchOccurence";
import { ServiceBayScheduleDto } from "../dtos/serviceBayDto";

export const useDealershipConsultantSchedule = () => {
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [scheduleSidebar, setScheduleSidebar] = useState(true);
  const [consultantMonthSchedule, setConsultantMonthSchedule] = useState<
    Scheduling[]
  >([]);
  const [scheduleViewType, setScheduleViewType] = useState("week");
  const [unviewedScheduleModal, setUnviwedScheduleModal] = useState(false);
  const [unviwedSchedulings, setUnviwedSchedulings] = useState<Scheduling[]>(
    []
  );
  const [occurrenceUuid, setOccurrenceUuid] = useState<string>("");
  const [dn, setDn] = useState<string>("");

  useEffect(() => {
    const fetchNewsSchedules = async () => {
      try {
        const unviewedSchedules = await fetchUnviewedSchedulings();

        if (unviewedSchedules.length > 0) {
          setUnviwedScheduleModal(true);
          setUnviwedSchedulings(unviewedSchedules);
        }
      } catch (error) {
        console.error("Erro ao buscar novos agendamentos:", error);
      }
    };

    const fetchSchedule = async () => {
      try {
        const consultantMonthSchedule =
          await fetchConsultantSchedulingsByMonthAndYear(
            currentDate.getMonth() + 1,
            currentDate.getFullYear()
          );

        // Get occurrences to check if the scheduling is in maintenance
        const chassisListFilter = consultantMonthSchedule
          .map((scheduling) => scheduling.asset?.chassis)
          .filter(Boolean) as string[];
        const finishedOccurrences = await fetchOccurenceList(
          { chassisListFilter },
          "CONSULTANT",
          0,
          100,
          true
        );
        const inProgressOccurrences = await fetchOccurenceList(
          { chassisListFilter },
          "CONSULTANT",
          0,
          100,
          false
        );

        setConsultantMonthSchedule(
          consultantMonthSchedule.map((scheduling) => {
            const finishedOccurrence = finishedOccurrences.occurrences.find(
              (occurrence) =>
                occurrence.vehicleChassis === scheduling.asset?.chassis
            );
            const inProgressOccurrence = inProgressOccurrences.occurrences.find(
              (occurrence) =>
                occurrence.vehicleChassis === scheduling.asset?.chassis
            );
            const maintenanceStatus = finishedOccurrence
              ? "FINISHED"
              : inProgressOccurrence
              ? "IN_PROGRESS"
              : "PENDING";

            // If the maintenance is not pending, get the service bay schedule
            const serviceBaySchedule =
              maintenanceStatus !== "PENDING" &&
              (inProgressOccurrence?.serviceBaySchedule ||
                finishedOccurrence?.serviceBaySchedule)
                ? ServiceBayScheduleDto(
                    inProgressOccurrence?.serviceBaySchedule ??
                      finishedOccurrence?.serviceBaySchedule
                  )
                : undefined;

            return {
              ...scheduling,
              maintenanceStatus,
              serviceBaySchedule,
              occurrenceId: inProgressOccurrence?.id ?? finishedOccurrence?.id,
            };
          })
        );
      } catch (error) {
        console.error("Erro ao buscar agendamentos:", error);
      }

      setLoading(false);
    };

    fetchSchedule();
    fetchNewsSchedules();
  }, [currentDate]);

  const closeUnviewdSchedulingsDialog = () => {
    for (const scheduling of unviwedSchedulings) {
      checkSchedulingAsViewed(scheduling.id);
    }
    setUnviwedScheduleModal(false);
  };

  const handleSaveSchedule = (data: any) => {
    setOccurrenceUuid(data.occurrenceUuid);
    setDn(data.dn);
  };

  return {
    loading,
    currentDate,
    scheduleSidebar,
    consultantMonthSchedule,
    scheduleViewType,
    unviewedScheduleModal,
    unviwedSchedulings,
    closeUnviewdSchedulingsDialog,
    setCurrentDate,
    setScheduleSidebar,
    setScheduleViewType,
    occurrenceUuid,
    dn,
    handleSaveSchedule,
  };
};

export const useDealershipConsultantDaySchedule = (
  firstWeekDay: Date,
  consultantMonthSchedule: Scheduling[],
  handleScheduling: (scheduling: Scheduling) => void
) => {
  const navigate = useNavigate();
  const [weekSchedule, setWeekSchedule] = useState<WeekSchedule | undefined>(
    undefined
  );

  useEffect(() => {
    const fetchWeekSchedule = async () => {
      const weekSchedule = await getMaintenanceWeekSchedule(
        firstWeekDay,
        consultantMonthSchedule
      );
      setWeekSchedule(weekSchedule);
    };
    fetchWeekSchedule();
  }, [firstWeekDay, consultantMonthSchedule]);

  const getDaySchedule = (date: Date): DaySchedule | undefined =>
    weekSchedule?.days.find((daySchedule) => {
      const day = new Date(daySchedule.date);
      return (
        day.getFullYear() === date.getFullYear() &&
        day.getMonth() === date.getMonth() &&
        day.getDate() === date.getDate()
      );
    });

  const getScheduleTask = (
    date: Date,
    hour: number,
    minutes: number = 0
  ): JSX.Element | undefined => {
    const daySchedule = getDaySchedule(date);
    if (!daySchedule) return undefined;

    const task = daySchedule.schedulings.find((task) => {
      const taskDate = new Date(task.date);
      return taskDate.getHours() === hour && taskDate.getMinutes() === minutes;
    });

    return task ? (
      <SchedulingCard task={task} handleScheduling={handleScheduling} />
    ) : (
      <div
        className="width-100pct height-100pct cursor-pointer"
        onClick={() =>
          navigate(
            `/dealership/appointments/appointment?date=${new Date(
              date.setHours(hour - 3, minutes, 0, 0)
            ).toISOString()}`
          )
        }
      />
    );
  };

  return { getScheduleTask };
};

export const useDealershipConsultantWeekSchedule = (
  firstWeekDay: Date,
  consultantMonthSchedule: Scheduling[],
  handleScheduling: (scheduling: Scheduling) => void
) => {
  const navigate = useNavigate();

  const [weekSchedule, setWeekSchedule] = useState<WeekSchedule | undefined>(
    undefined
  );

  useEffect(() => {
    const fetchWeekSchedule = async () => {
      const weekSchedule = await getMaintenanceWeekSchedule(
        firstWeekDay,
        consultantMonthSchedule
      );
      setWeekSchedule(weekSchedule);
    };
    fetchWeekSchedule();
  }, [firstWeekDay, consultantMonthSchedule]);

  const getDaySchedule = (date: Date): DaySchedule | undefined =>
    weekSchedule?.days.find((daySchedule) => {
      const day = new Date(daySchedule.date);
      return (
        day.getFullYear() === date.getFullYear() &&
        day.getMonth() === date.getMonth() &&
        day.getDate() === date.getDate()
      );
    });

  const getScheduleTask = (
    date: Date,
    hour: number,
    minutes: number = 0
  ): JSX.Element | undefined => {
    const daySchedule = getDaySchedule(date);
    if (!daySchedule) return undefined;

    const task = daySchedule.schedulings.find((task) => {
      const taskDate = new Date(task.date);
      return taskDate.getHours() === hour && taskDate.getMinutes() === minutes;
    });

    return task ? (
      <SchedulingCard task={task} handleScheduling={handleScheduling} />
    ) : (
      <div
        className="width-100pct height-100pct cursor-pointer"
        onClick={() =>
          navigate(
            `/dealership/appointments/appointment?date=${new Date(
              date.setHours(hour - 3, minutes, 0, 0)
            ).toISOString()}`
          )
        }
      />
    );
  };

  return { getScheduleTask };
};
